import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, type Column } from "@/components/ui/data-table";
import { PageState } from "@/components/common/PageState";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePlatformPermissions } from "@/hooks/usePlatformPermissions";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { formatDateTime } from "@/utils/dateUtils";
import type {
  PartnerAiState,
  PartnerDestinationSummary,
} from "@/types/partner-destination";
import type { PartnerSyncState } from "@/types/partner-settings";
import { describeError } from "./utils/describeError";
import {
  useBackfillAi,
  useDestinations,
  useDestinationSync,
} from "./hooks/partner-destinations.hooks";

const syncStateBadgeVariant = (
  state: PartnerSyncState,
): "secondary" | "success" | "destructive" | "outline" => {
  if (state === "succeeded") return "success";
  if (state === "failed") return "destructive";
  if (state === "partial") return "outline";
  return "secondary";
};

const aiStateBadgeVariant = (
  state: PartnerAiState,
): "secondary" | "success" | "destructive" | "outline" => {
  if (state === "succeeded") return "success";
  if (state === "failed") return "destructive";
  if (state === "running" || state === "queued") return "outline";
  return "secondary";
};

/**
 * Task 0032, contract 2026-09-29: Cities grouped by country, sections
 * sorted country -> city. A destination with no known country (`null`)
 * groups under a fixed "Other" section, sorted last.
 */
interface DestinationCountryGroup {
  countryCode: string | null;
  countryName: string | null;
  destinations: PartnerDestinationSummary[];
}

function groupDestinationsByCountry(
  destinations: PartnerDestinationSummary[],
  unknownCountryLabel: string,
): DestinationCountryGroup[] {
  const groups = new Map<string, DestinationCountryGroup>();

  for (const destination of destinations) {
    const key = destination.countryCode ?? destination.countryName ?? "";
    let group = groups.get(key);
    if (!group) {
      group = {
        countryCode: destination.countryCode,
        countryName: destination.countryName,
        destinations: [],
      };
      groups.set(key, group);
    }
    group.destinations.push(destination);
  }

  for (const group of groups.values()) {
    group.destinations.sort((a, b) => a.name.localeCompare(b.name));
  }

  return Array.from(groups.values())
    .sort((a, b) => {
      if (!a.countryName) return 1;
      if (!b.countryName) return -1;
      return a.countryName.localeCompare(b.countryName);
    })
    .map((group) => ({
      ...group,
      countryName: group.countryName ?? unknownCountryLabel,
    }));
}

const DestinationSyncButton: React.FC<{
  destinationId: string;
  canWrite: boolean;
}> = ({ destinationId, canWrite }) => {
  const { t } = useTranslation();
  const { isSyncing, triggerSync } = useDestinationSync(destinationId);

  return (
    <Button
      variant="outline"
      size="sm"
      disabled={!canWrite || isSyncing}
      title={
        canWrite
          ? undefined
          : t("common.insufficientPermissions", "Insufficient permissions")
      }
      onClick={(event) => {
        event.stopPropagation();
        triggerSync();
      }}
    >
      <RefreshCw className={isSyncing ? "size-4 animate-spin" : "size-4"} />
      {isSyncing
        ? t("partners.admin.destinations.syncing")
        : t("partners.admin.destinations.sync")}
    </Button>
  );
};

const BackfillAiButton: React.FC = () => {
  const { t } = useTranslation();
  const { backfill, isBackfilling } = useBackfillAi();

  return (
    <Button
      variant="outline"
      disabled={isBackfilling}
      onClick={() => {
        if (
          window.confirm(
            t("partners.admin.destinations.ai.backfillDialogDescription"),
          )
        ) {
          backfill();
        }
      }}
    >
      {t("partners.admin.destinations.ai.backfill")}
    </Button>
  );
};

export const DestinationsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canAdminister } = usePlatformPermissions();
  const canWrite = canAdminister("partners");
  const { userProfile } = useAuthenticatedUser();
  const isSuperAdmin = userProfile?.role === "SUPER_ADMIN";
  const {
    data: destinations = [],
    isLoading,
    isError,
    error,
  } = useDestinations();

  const countryGroups = useMemo(
    () =>
      groupDestinationsByCountry(
        destinations,
        t("partners.admin.destinations.unknownCountry"),
      ),
    [destinations, t],
  );

  const columns: Column<PartnerDestinationSummary>[] = [
    {
      key: "name",
      header: t("partners.admin.destinations.table.name"),
      render: (value: string) => (
        <span className="text-foreground font-medium">{value}</span>
      ),
    },
    {
      key: "hotelsCount",
      header: t("partners.admin.destinations.table.hotelsCount"),
    },
    {
      key: "curatedEnabled",
      header: t("partners.admin.destinations.table.curated"),
      render: (value: boolean, row) => (
        <div className="flex items-center gap-2">
          <Badge variant={value ? "success" : "secondary"}>
            {value
              ? t("partners.admin.destinations.curatedOn")
              : t("partners.admin.destinations.curatedOff")}
          </Badge>
          <span className="text-muted-foreground text-sm">
            {t("partners.admin.destinations.table.curatedCount", {
              count: row.curatedCount,
            })}
          </span>
        </div>
      ),
    },
    {
      key: "cacheCount",
      header: t("partners.admin.destinations.table.cacheCount"),
      render: (value: number) => {
        if (value > 0) return value;
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-destructive inline-flex items-center gap-1 font-medium">
                {value}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {t("partners.admin.destinations.table.cacheCountEmptyHint")}
            </TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      key: "lastSyncAt",
      header: t("partners.admin.destinations.table.lastSyncAt"),
      render: (value: string | null) =>
        value
          ? formatDateTime(value)
          : t("partners.admin.destinations.neverSynced"),
    },
    {
      key: "syncState",
      header: t("partners.admin.destinations.table.syncState"),
      render: (value: PartnerSyncState) => (
        <Badge variant={syncStateBadgeVariant(value)}>
          {t(`partners.admin.destinations.syncStateValues.${value}`, {
            defaultValue: value,
          })}
        </Badge>
      ),
    },
    {
      key: "aiState",
      header: t("partners.admin.destinations.table.aiState"),
      render: (value: PartnerAiState, row) => (
        <div className="flex items-center gap-2">
          <Badge variant={aiStateBadgeVariant(value)}>
            {t(`partners.admin.destinations.aiStateValues.${value}`, {
              defaultValue: value,
            })}
          </Badge>
          <span className="text-muted-foreground text-xs">
            {row.aiRunAt
              ? formatDateTime(row.aiRunAt)
              : t("partners.admin.destinations.ai.lastRunNever")}
          </span>
        </div>
      ),
    },
    {
      key: "actions",
      header: t("partners.admin.destinations.table.actions"),
      className: "w-[140px]",
      render: (_value: unknown, row) => (
        <div onClick={(event) => event.stopPropagation()}>
          <DestinationSyncButton
            destinationId={row.destinationId}
            canWrite={canWrite}
          />
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <PageState text={t("partners.admin.destinations.loading")} />;
  }

  if (isError) {
    return (
      <PageState
        text={describeError(t("partners.admin.destinations.loadError"), error)}
        tone="error"
      />
    );
  }

  return (
    <div className="mx-auto flex flex-col gap-6 p-12">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-foreground text-2xl font-semibold">
            {t("partners.admin.destinations.title")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("partners.admin.destinations.description")}
          </p>
        </div>
        {isSuperAdmin && <BackfillAiButton />}
      </div>

      {destinations.length === 0 ? (
        <DataTable
          data={[]}
          columns={columns}
          emptyMessage={t("partners.admin.destinations.table.empty")}
          getRowKey={(row) => row.destinationId}
          withPagination={false}
          withSorting={false}
        />
      ) : (
        countryGroups.map((group) => (
          <div
            key={group.countryCode ?? group.countryName}
            className="flex flex-col gap-2"
          >
            <h2 className="text-foreground flex items-center gap-2 text-lg font-semibold">
              {group.countryName}
              <span className="text-muted-foreground text-sm font-normal">
                ({group.destinations.length})
              </span>
            </h2>
            <DataTable
              data={group.destinations}
              columns={columns}
              emptyMessage={t("partners.admin.destinations.table.empty")}
              getRowKey={(row) => row.destinationId}
              onRowClick={(row) =>
                navigate(`/partners/destinations/${row.destinationId}`)
              }
              withPagination={false}
              withSorting={false}
            />
          </div>
        ))
      )}
    </div>
  );
};
